/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/usePagingGroupsList.ts
 🗓️ Created: 2025-01-22
 🧩 Description:
    Custom hook for managing Paging Groups list.
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useDataGrid, type DataGridFetchResult } from '../../../hooks/useDataGrid';
import { fetchPagingGroups } from '../api/pagingGroupsApi';

interface UsePagingGroupsListOptions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialColumns?: any[];
  initialSortBy?: string;
  initialSortOrder?: 'asc' | 'desc';
  initialLimit?: number;
  domainId?: number;
}

export function usePagingGroupsList(options: UsePagingGroupsListOptions = {}) {
  const {
    initialColumns = [],
    initialSortBy = 'code',
    initialSortOrder = 'asc',
    initialLimit = 10,
    domainId,
  } = options;

  const sortableColumns = new Set(['code', 'name', 'mode', 'enabled', 'createdAt', 'updatedAt']);

  const fetchData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      const safeSortBy =
        params.sortBy && sortableColumns.has(params.sortBy) ? params.sortBy : initialSortBy;
      const safeSortOrder = params.sortOrder === 'desc' ? 'desc' : 'asc';

      const response = await fetchPagingGroups({
        page: params.page,
        limit: params.limit,
        sortBy: safeSortBy,
        sortOrder: safeSortOrder,
        search: params.search?.trim(),
        domainId,
      });
      return {
        data: response.data || [],
        pagination: response.pagination || {
          page: params.page,
          limit: params.limit,
          total: 0,
          totalPages: 0
        }
      } as DataGridFetchResult;
    },
    [sortableColumns, initialSortBy, domainId]
  );

  const dataGrid = useDataGrid({
    fetchData,
    initialColumns,
    initialSortBy,
    initialSortOrder,
    initialLimit,
    moduleName: 'paging-groups',
  });

  // Refresh data when domainId changes
  useEffect(() => {
    if (domainId !== undefined) {
      dataGrid.refresh();
    }
  }, [domainId]);

  return dataGrid;
}

